// SignIn.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";

import withAuthentication from "../../hoc/withAuthentication";
import Footer from "../../components/Footer/Footer";
import SignInForm from "./SignInForm";
import { SignInMainContainer } from "./SignInDesign";

const DEV_PASS = "only";

function SignIn() {
  /* ---------------- gate dialog state ---------------- */
  const [unlocked, setUnlocked] = useState(false); // false → show dialog first
  const [passValue, setPassValue] = useState("");
  const [err, setErr] = useState("");

  const verifyPassword = () => {
    if (passValue === DEV_PASS) setUnlocked(true);
    else setErr("Incorrect password");
  };

  /* ---------------- responsive helpers ---------------- */
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  /* ---------------- sign-in page content --------------- */
  const SignInLayout = (
    <>
      <SignInMainContainer
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: "100vh",
        }}
      >
        {/* Left pane = decorative content */}
        {!isMobile && (
          <Box
            flex={1}
            sx={{
              bgcolor: "#F5F5FA",
              borderRadius: { xs: 0, md: "24px 0 0 24px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 4, md: 8 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(90deg,#6366F1,#9F03FF)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                StageLink
              </Typography>

              <Typography variant="h4" fontWeight={700} mt={4} lineHeight={1.4}>
                <span
                  style={{
                    background: "linear-gradient(90deg,#6366F1,#3B82F6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Empowering Passion.
                </span>
                <br />
                <span
                  style={{
                    background: "linear-gradient(90deg,#A044FF,#FF6BFF)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Connecting the World.
                </span>
              </Typography>
            </motion.div>
          </Box>
        )}

        {/* Right pane = actual sign-in form */}
        <Box
          flex={1}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 4, md: 8 },
            bgcolor: "#FDFDFE",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            style={{ width: "100%", maxWidth: 550 }}
          >
            <SignInForm />
          </motion.div>
        </Box>
      </SignInMainContainer>

      <Footer />
    </>
  );

  /* ---------------- render ---------------------------- */
  return (
    <>
      {/* Developer-password dialog – visible until unlocked */}
      {!unlocked && (
        <Dialog open fullWidth maxWidth="xs">
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Developer Access
            <IconButton size="small" onClick={() => setErr("")}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2" mb={2}>
              This environment is restricted. Enter the developer password.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              label="Password"
              type="password"
              value={passValue}
              error={!!err}
              helperText={err}
              onChange={(e) => {
                setErr("");
                setPassValue(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") verifyPassword();
              }}
            />
          </DialogContent>

          <DialogActions sx={{ pr: 3, pb: 2 }}>
            <Button variant="contained" onClick={verifyPassword}>
              Enter
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Actual sign-in page, only after unlock */}
      {unlocked && SignInLayout}
    </>
  );
}

export default withAuthentication(SignIn, ["/sign-in"]);
